import React, { useState, useEffect, useMemo, useRef } from 'react'
import { CollectionItem } from '../../types/collections'
import { useCollection } from '../../hooks/useCollection'
import { useCollectionItems } from '../../hooks/useCollectionItemsList'
import { useRankedPeople } from '../../hooks/useRankedPeople'
import { collectionItemNoun, renderCollectionItem } from '../../utils/collectionUtils'
import { collectionsApi } from '../../services/collectionsApi'
import { computeSubtitles } from '../../utils/pickerSubtitles'
import { splitPersonName } from '../../utils/people'
import PickerRow, { PickerRowUsage } from '../PickerRow'

/** Rows the People empty state shows (server cap for `/api/people/ranked`). */
const PEOPLE_EMPTY_STATE_LIMIT = 25

interface ReferenceSelectorProps {
    collectionId: number
    value?: number
    onChange: (itemId?: number) => void
    onClose: () => void
    allowCreate?: boolean
}

export const ReferenceSelector: React.FC<ReferenceSelectorProps> = ({
    collectionId,
    value,
    onChange,
    onClose,
    allowCreate = true
}) => {
    const [search, setSearch] = useState('')
    const [selectedId, setSelectedId] = useState<number | undefined>(value)
    const [isCreating, setIsCreating] = useState(false)
    const [newItemData, setNewItemData] = useState<Record<string, any>>({})
    const searchRef = useRef<HTMLInputElement>(null)
    const selectorRef = useRef<HTMLDivElement>(null)
    const createFormRef = useRef<HTMLDivElement>(null)
    
    const { data: collection } = useCollection(collectionId)
    const isPeople = collection?.system_kind === 'people'
    // Default (empty-search) list: the most recent items, capped server-side.
    // Deferred until the collection loads (rows can't render without its
    // config anyway) so People — whose empty state is the ranked list below —
    // never fires a list fetch it won't display.
    const { data: items = [], isLoading } = useCollectionItems(
        collection && !isPeople ? collectionId : 0,
    )
    // People's empty state is ranked (most-tagged-first) instead of
    // created-at, so the default list carries usage counts too. Disabled for
    // every other collection — the hook then skips the fetch entirely.
    const { ranked, loading: rankedLoading } = useRankedPeople(
        PEOPLE_EMPTY_STATE_LIMIT,
        isPeople,
    )
    const rankedItems: CollectionItem[] = useMemo(() => {
        if (!isPeople || !ranked) return []
        return ranked.lifetime.map(person => ({
            id: person.collection_item_id,
            collection_id: collectionId,
            data: person.data as CollectionItem['data'],
            // Same metadata shape the search path returns (snake_case), so
            // both lists flow through one usage mapping below. The ranked
            // endpoint doesn't carry last_tagged_at; the line shows the count.
            metadata: { usage: { checkin_count: person.checkin_count } },
            created_at: '',
            updated_at: '',
        }))
    }, [isPeople, ranked, collectionId])

    // When the user types, search the whole collection server-side rather than
    // filtering the capped default list — otherwise items outside the first
    // page (e.g. older People) are invisible to the picker.
    const [searchResults, setSearchResults] = useState<CollectionItem[]>([])
    const [isSearching, setIsSearching] = useState(false)

    useEffect(() => {
        const q = search.trim()
        if (!q) {
            setSearchResults([])
            setIsSearching(false)
            return
        }
        setIsSearching(true)
        let cancelled = false
        const handle = setTimeout(async () => {
            try {
                const results = await collectionsApi.items.search(collectionId, q)
                if (!cancelled) setSearchResults(results)
            } catch (error) {
                console.error('Failed to search collection items:', error)
                if (!cancelled) setSearchResults([])
            } finally {
                if (!cancelled) setIsSearching(false)
            }
        }, 200)
        return () => {
            cancelled = true
            clearTimeout(handle)
        }
    }, [search, collectionId])
    
    // Focus the search input on MOUNT ONLY, and register the click-outside
    // listener once (reading onClose through a ref). Depending on `onClose`
    // here re-ran the effect on every parent re-render — parents pass inline
    // lambdas — and the stray `.focus()` stole focus from the create form
    // mid-typing.
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose
    useEffect(() => {
        searchRef.current?.focus()

        // Handle clicking outside to close
        const handleClickOutside = (event: MouseEvent) => {
            if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
                onCloseRef.current()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])
    
    // Auto-scroll to the create form when it appears
    useEffect(() => {
        if (isCreating && createFormRef.current) {
            createFormRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
        }
    }, [isCreating])
    
    // Empty search → recent items (ranked for People); otherwise →
    // server-side search results (matched against all data fields, not just
    // the display template).
    const filteredItems = search.trim() ? searchResults : isPeople ? rankedItems : items
    const isListLoading = search.trim()
        ? isSearching
        : isPeople
            ? rankedLoading
            : isLoading

    // Differentiating-fields subtitles for the displayed rows (e.g. two
    // "Maria"s get their `relation`), computed over whatever list is showing.
    const subtitles = useMemo(
        () =>
            collection
                ? computeSubtitles(
                      filteredItems.map(item => ({ id: item.id, data: item.data })),
                      collection.config,
                  )
                : new Map<number | string, string>(),
        [collection, filteredItems],
    )

    // Usage line from search enrichment (`metadata.usage`, snake_case) or the
    // ranked People mapping above. Plain `items.list` rows carry no metadata,
    // so generic collections' empty state shows no usage line.
    const getUsage = (item: CollectionItem): PickerRowUsage | undefined => {
        const usage = item.metadata?.usage
        if (!usage || typeof usage.checkin_count !== 'number') return undefined
        return {
            checkinCount: usage.checkin_count,
            lastTaggedAt: usage.last_tagged_at ?? null,
        }
    }
    
    const handleSelect = (itemId: number) => {
        setSelectedId(itemId)
        onChange(itemId)
        onClose()
    }

    // "person" / "place" for the system collections, generic "item" elsewhere.
    const itemNoun = collectionItemNoun(collection)
    const itemNounPlural = itemNoun === 'person' ? 'people' : `${itemNoun}s`

    // Create-form fields in display order; the first string field (People:
    // "first name", Places: "name") is seeded from the search text and
    // autofocused when the form opens.
    const sortedFields = useMemo(
        () =>
            collection
                ? Object.entries(collection.config.fields)
                      .sort(([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0))
                : [],
        [collection],
    )
    const seedFieldName = useMemo(
        () => sortedFields.find(([, field]) => field.type === 'string')?.[0] ?? sortedFields[0]?.[0],
        [sortedFields],
    )
    // People only: the companion "last name" field, so a search of "Joe Blow"
    // fills both name fields rather than one. Matched case-insensitively —
    // the schema's canonical key is lowercase but a user may have renamed it
    // (`ensure_canonical_fields` treats "Last Name" as the same field).
    const lastNameFieldName = useMemo(
        () =>
            isPeople
                ? sortedFields.find(
                      ([name, field]) =>
                          field.type === 'string' && name.trim().toLowerCase() === 'last name',
                  )?.[0]
                : undefined,
        [isPeople, sortedFields],
    )

    const startCreating = () => {
        const seed = search.trim()
        if (!seed || !seedFieldName) {
            setNewItemData({})
        } else if (lastNameFieldName && lastNameFieldName !== seedFieldName) {
            const { first, last } = splitPersonName(seed)
            setNewItemData(
                last ? { [seedFieldName]: first, [lastNameFieldName]: last } : { [seedFieldName]: first },
            )
        } else {
            setNewItemData({ [seedFieldName]: seed })
        }
        setIsCreating(true)
    }

    const handleCreate = async () => {
        if (!collection) return
        
        try {
            const response = await collectionsApi.items.create(collectionId, {
                data: {
                    ...newItemData,
                    schema_version: collection.config.schema_version
                }
            })
            handleSelect(response.id)
        } catch (error) {
            console.error('Failed to create collection item:', error)
        }
    }
    
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
        }
    }
    
    // Get picture field for display if available
    const getPictureUrl = (item: CollectionItem): string | undefined => {
        if (!collection) return undefined
        
        const pictureFields = Object.entries(collection.config.fields)
            .filter(([_, field]) => field.type === 'picture')
            .map(([name, _]) => name)
        
        if (pictureFields.length > 0) {
            return item.data[pictureFields[0]]
        }
        
        return undefined
    }
    
    return (
        <div ref={selectorRef} className="absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 w-96 max-h-96 flex flex-col">
            <div className="p-3 border-b border-gray-200 flex-shrink-0">
                <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={`Search ${itemNounPlural}...`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action"
                />
            </div>
            
            <div className="flex-1 overflow-y-auto min-h-0">
                {isListLoading ? (
                    <div className="p-4 text-center text-gray-500">Loading...</div>
                ) : filteredItems.length > 0 ? (
                    <div className="py-1">
                        {filteredItems.map(item => {
                            const isSelected = item.id === selectedId

                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleSelect(item.id)}
                                    className={`w-full text-left px-3 py-2 hover:bg-gray-100 ${
                                        isSelected ? 'bg-amber-50 hover:bg-amber-100' : ''
                                    }`}
                                >
                                    <PickerRow
                                        primary={collection ? renderCollectionItem(item, collection) : `#${item.id}`}
                                        primaryClassName={isSelected ? 'text-amber-900' : ''}
                                        subtitle={subtitles.get(item.id)}
                                        usage={getUsage(item)}
                                        avatarData={isPeople ? item.data : undefined}
                                        pictureUrl={isPeople ? undefined : getPictureUrl(item)}
                                        trailing={
                                            isSelected ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-5 w-5 text-amber-800 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            ) : undefined
                                        }
                                    />
                                </button>
                            )
                        })}
                    </div>
                ) : (
                    <div className="p-4 text-center text-gray-500">
                        No {itemNounPlural} found
                    </div>
                )}
            </div>
            
            {allowCreate && collection && (
                <div className="p-3 border-t border-gray-200 flex-shrink-0 max-h-64 overflow-y-auto">
                    {!isCreating ? (
                        <button
                            onClick={startCreating}
                            className="w-full text-left px-3 py-2 text-amber-800 hover:bg-amber-50 rounded-md"
                        >
                            + Create new {itemNoun}
                        </button>
                    ) : (
                        <div ref={createFormRef} className="space-y-2">
                            <div className="text-sm font-medium text-gray-700">Create new {itemNoun}</div>
                            {sortedFields.map(([fieldName, field]) => (
                                <div key={fieldName}>
                                    <label className="block text-xs text-gray-600 mb-1">
                                        {fieldName}{field.required && ' *'}
                                    </label>
                                    <input
                                        type={field.type === 'int' || field.type === 'decimal' ? 'number' : 'text'}
                                        autoFocus={fieldName === seedFieldName}
                                        value={newItemData[fieldName] || ''}
                                        onChange={(e) => setNewItemData({
                                            ...newItemData,
                                            [fieldName]: e.target.value
                                        })}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-action"
                                        placeholder={field.description}
                                    />
                                </div>
                            ))}
                            <div className="flex gap-2 pt-2">
                                <button
                                    onClick={handleCreate}
                                    className="px-3 py-1 text-sm bg-action text-plum rounded hover:bg-action-dark"
                                >
                                    Create
                                </button>
                                <button
                                    onClick={() => {
                                        setIsCreating(false)
                                        setNewItemData({})
                                    }}
                                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}